import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { UICard, UIDateRangeFields } from "../../../../../ui";
import EventPayrollBulkSummaryCards from "./EventPayrollBulkSummaryCards";
import EventPayrollBulkTable from "./EventPayrollBulkTable";
import EventPayrollBulkShiftTabs from "./EventPayrollBulkShiftTabs";
import EventPayrollBulkRatesLegend from "./EventPayrollBulkRatesLegend";

import "../../../../../Card/_Card.scss";

const EventPayrollBulkPayrollReportView = ({
  data,
  applicantCount,
  labels,
  shared,
  showStcNumber,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [activeShift, setActiveShift] = useState(data.shifts[0]);

  const summaryItems = useMemo(
    () => [
      {
        key: "deductions",
        label: labels.summary.totalDeductions,
        value: data.summary.totalDeductions,
        tone: "destructive",
      },
      {
        key: "rewards",
        label: labels.summary.totalRewards,
        value: data.summary.totalRewards,
        tone: "success",
      },
      {
        key: "netPay",
        label: labels.summary.totalNetPay,
        value: data.summary.totalNetPay,
        tone: "primary",
        isCurrency: true,
        isTotalSalary: true,
      },
    ],
    [data.summary, labels.summary],
  );

  return (
    <div className="event-payroll-generate__bulk">
      <header className="event-payroll-generate__page-header">
        <h2 className="event-payroll-generate__page-header-title text-heading-xl">
          {labels.bulkTitle}
        </h2>
        <p className="event-payroll-generate__page-header-subtitle text-body-sm">
          {labels.bulkSubtitle.replace("{count}", String(applicantCount))}
        </p>
      </header>

      <UICard
        type="info"
        className="event-payroll-generate__header-card ui-card--section"
      >
        <div className="event-payroll-generate__header-card-inner">
          <div className="event-payroll-generate__period event-payroll-generate__period--inline">
            <span className="event-detail-label">{labels.reportPeriod}</span>
            <UIDateRangeFields
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={onStartDateChange}
              onEndDateChange={onEndDateChange}
              startPlaceholder={labels.startDate}
              endPlaceholder={labels.endDate}
              rangeErrorMessage={labels.endDateBeforeStartDate}
              classNames={{
                start: "event-payroll-generate__period-date",
                end: "event-payroll-generate__period-date",
              }}
            />
          </div>

          <EventPayrollBulkSummaryCards
            items={summaryItems}
            currency={labels.summary.currency}
          />
        </div>
      </UICard>

      <EventPayrollBulkTable
        rows={data.applicants}
        labels={labels.columns}
        currency={labels.summary.currency}
        showStcNumber={showStcNumber}
        rangeTemplate={labels.rangeTemplate}
        rowsPerPageLabel={shared.numberOfRows}
      />

      {data.shifts.length > 0 && (
        <UICard
          type="info"
          className="event-payroll-generate__legend event-payroll-generate__header-card ui-card--section"
        >
          <EventPayrollBulkShiftTabs
            shifts={data.shifts}
            activeShift={activeShift}
            onChange={setActiveShift}
            labels={labels.shifts}
          />

          <EventPayrollBulkRatesLegend
            roleRates={data.roleRatesByShift?.[activeShift] || []}
            trainingRates={data.trainingRatesByShift?.[activeShift] || []}
            labels={labels}
          />
        </UICard>
      )}
    </div>
  );
};

EventPayrollBulkPayrollReportView.propTypes = {
  data: PropTypes.object.isRequired,
  applicantCount: PropTypes.number,
  labels: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  showStcNumber: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
};

EventPayrollBulkPayrollReportView.defaultProps = {
  applicantCount: 0,
  showStcNumber: false,
  startDate: "",
  endDate: "",
  onStartDateChange: undefined,
  onEndDateChange: undefined,
};

export default EventPayrollBulkPayrollReportView;
